import React, { useState, useEffect } from 'react';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';
import DataTable from '../../../components/Content/DataTable';
import DateFilterTable from '../../../components/Content/DateFilterTable';

// Date & Time
import moment from 'moment';

// Utils
import { getAlert } from '../../../../utils/SamuiUtils';
import {
    useDataTableKeyboardNavigation
} from '../../../../utils/DataTableKeyboardNavigation';
import { ExportSo } from '../../../../utils/ExportUtils';

function Main({ masterList, statusColours, statusPaidColours, name, onPageInsert, onRowSelected, getReport, startDate, endDate, setStartDate, setEndDate }) {
    const [dataMasterList, setDataMasterList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(localStorage.getItem('itemsPerPageDropdown') || 10);
    const [countWaitApprove, setCountWaitApprove] = useState(0);
    const [countApprove, setCountApprove] = useState(0);
    const [countSuccess, setCountSuccess] = useState(0);
    const [countCancel, setCountCancel] = useState(0);
    const [countOverduePaid, setCountOverduePaid] = useState(0);
    const [countPaidInFull, setCountPaidInFull] = useState(0);

    // ใช้สำหรับค้นหาเอกสาร
    const [searchTerm, setSearchTerm] = useState(localStorage.getItem('searchTerm') || '');
    const [filteredItems, setFilteredItems] = useState([]);


    const [selectedStatus, setSelectedStatus] = useState(null);

    // DataTable keyboard navigation state
    const [selectedRowIndex, setSelectedRowIndex] = useState(0);

    useEffect(() => {
        initialize();
    }, [masterList]);

    useEffect(() => {
        setCurrentPage(1);
        filterItems();
    }, [searchTerm, dataMasterList, selectedStatus, startDate, endDate]);

    const initialize = async () => {
        try {
            const normalizedList = Array.isArray(masterList) ? [...masterList] : [];
            setDataMasterList(normalizedList);

            // นับข้อมูลสถานะ
            let statusCounts = {
                'รอยืนยัน': 0,
                'ยืนยัน': 0,
                'สำเร็จ': 0,
                'ยกเลิก': 0
            };

            let statusPaidCounts = {
                'ค้างจ่าย': 0,
                'จ่ายครบ': 0
            };

            normalizedList.forEach(data => {
                if (data.DocStatus_Name) {
                    statusCounts[data.DocStatus_Name] = (statusCounts[data.DocStatus_Name] || 0) + 1;
                }
            });

            normalizedList.forEach(data => {
                if (data.DocStatusPaid_Name && data.DocStatus_Name !== 'ยกเลิก') {
                    statusPaidCounts[data.DocStatusPaid_Name] = (statusPaidCounts[data.DocStatusPaid_Name] || 0) + 1;
                }
            });

            setCountWaitApprove(statusCounts['รอยืนยัน']);
            setCountApprove(statusCounts['ยืนยัน']);
            setCountSuccess(statusCounts['สำเร็จ']);
            setCountCancel(statusCounts['ยกเลิก']);

            setCountOverduePaid(statusPaidCounts['ค้างจ่าย']);
            setCountPaidInFull(statusPaidCounts['จ่ายครบ']);

            setCurrentPage(1); // รีเซ็ตหน้าเมื่อมีข้อมูลใหม่
        } catch (error) {
            getAlert('FAILED', error.message);
        }
    };

    // กรองข้อมูลตามสถานะที่เลือก
    const filterItems = () => {
        let filtered = dataMasterList;

        const start = moment(startDate, "YYYY-MM-DD");
        const end = moment(endDate, "YYYY-MM-DD");

        filtered = filtered.filter((item) => {
            const itemDate = moment(item.Doc_Date, "YYYY-MM-DD");
            return itemDate.isBetween(start, end, null, "[]");
        });

        if (searchTerm.trim() !== "") {
            const lowercasedSearchTerm = searchTerm.toLowerCase();
            filtered = filtered.filter((item) =>
                Object.values(item).some((value) => {
                    if (typeof value === "string") {
                        return value.toLowerCase().includes(lowercasedSearchTerm);
                    } else if (typeof value === "number") {
                        return value.toString().includes(searchTerm);
                    }
                    return false;
                })
            );
        }

        // ฟิลเตอร์ตามสถานะที่เลือก
        if (selectedStatus) {
            filtered = filtered.filter((item) => {
                // กรณีกรองสถานะรอจ่าย (ค้างจ่าย/จ่ายครบ) ต้องไม่แสดงรายการที่ถูกยกเลิก
                if (statusPaidColours.some((status) => status.DocSetStaus_Name === selectedStatus)) {
                    return item.DocStatusPaid_Name === selectedStatus && item.DocStatus_Name !== 'ยกเลิก';
                }
                if (statusColours.some((status) => status.DocSetStaus_Name === selectedStatus)) {
                    return item.DocStatus_Name === selectedStatus;
                }
                // กรณีสถานะรับสินค้า (ถ้ามี) สามารถเพิ่มได้ที่นี่
                return false;
            });
        }

        setFilteredItems(filtered);
    };

    // เมื่อคลิกสถานะ
    const handleStatusClick = (statusName) => {
        setSelectedStatus((prev) => (prev === statusName ? null : statusName));
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

    // Initialize keyboard navigation after currentItems is defined
    useDataTableKeyboardNavigation(
        true, // isActive
        currentItems,
        selectedRowIndex,
        setSelectedRowIndex,
        onRowSelected,
        {
            no: 'Doc_No',
            docBlNo: 'Doc_BL_No',
            docInvNo: 'Doc_INV_No'
        }, // fieldMappings
        currentPage,
        handlePageChange,
        filteredItems.length,
        itemsPerPage
    );

    // สามารถใช้กรองคำที่จะไม่ใช้ใน Counter Box ได้
    const filteredStatusColours = statusColours.filter(statusObj =>
        !['รอยืนยัน', 'ปิดงาน'].includes(statusObj.DocSetStaus_Name)
    );

    return (
        <>
            <div className="page-header d-flex justify-content-between align-items-center mb-0">
                <Breadcrumbs page={name} items={[
                    { name: 'ขายสินค้า', url: '/sellproducts' },
                    { name: name, url: '/billofsale' },
                ]} />
            </div>
            <div className="row hide-scrollbar" style={{ maxHeight: "auto", overflowY: "auto" }}>
                <div className="col-1">
                    <center>
                        <b>สถานะเอกสาร</b>
                    </center>
                    <br />
                    {statusColours.map((statusObj, index) => {
                        const { DocSetStaus_Name, DocSetColour } = statusObj;
                        let count;

                        switch (DocSetStaus_Name) {
                            case 'รอยืนยัน':
                                count = countWaitApprove;
                                break;
                            case 'ยืนยัน':
                                count = countApprove;
                                break;
                            case 'สำเร็จ':
                                count = countSuccess;
                                break;
                            case 'ยกเลิก':
                                count = countCancel;
                                break;
                            default:
                                count = 0;
                        }

                        return (
                            <div key={index} className="col-12 mb-3" onClick={() => handleStatusClick(DocSetStaus_Name)} style={{ cursor: "pointer" }}>
                                <div
                                    className="card text-center"
                                    style={{
                                        backgroundColor: DocSetColour,
                                        boxShadow: selectedStatus === DocSetStaus_Name ? "0 0 15px rgba(0,0,0,0.5)" : "none",
                                        border: selectedStatus === DocSetStaus_Name ? "2px solid #333" : "none",
                                        transform: selectedStatus === DocSetStaus_Name ? "scale(1.05)" : "scale(1)",
                                        transition: "transform 0.15s, box-shadow 0.15s, border 0.15s",
                                    }}
                                >
                                    <div className="card-body d-flex flex-column align-items-center text-white">
                                        <div className="row">
                                            <div className="col-12">
                                                <b style={{ fontSize: '20px', textAlign: 'center' }}>{count}</b>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-12">
                                                <b style={{ fontSize: '16px', textAlign: 'center' }}>{DocSetStaus_Name === 'ยืนยัน' ? 'ปกติ' : DocSetStaus_Name}</b>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <br />
                    <center>
                        <b>สถานะรอจ่าย</b>
                    </center>
                    <br />
                    {statusPaidColours
                        .filter(statusObj => statusObj.DocSetStaus_Name !== 'รอจ่าย')
                        .map((statusObj, index) => {
                            const { DocSetStaus_Name, DocSetColour } = statusObj;
                            let count;

                            switch (DocSetStaus_Name) {
                                case 'ค้างจ่าย':
                                    count = countOverduePaid;
                                    break;
                                case 'จ่ายครบ':
                                    count = countPaidInFull;
                                    break;
                                default:
                                    count = 0;
                            }

                            return (
                                <div key={index} className="col-12 mb-3" onClick={() => handleStatusClick(DocSetStaus_Name)} style={{ cursor: "pointer" }}>
                                    <div
                                        className="card text-center"
                                        style={{
                                            backgroundColor: DocSetColour,
                                            boxShadow: selectedStatus === DocSetStaus_Name ? "0 0 15px rgba(0,0,0,0.5)" : "none",
                                            border: selectedStatus === DocSetStaus_Name ? "2px solid #333" : "none",
                                            transform: selectedStatus === DocSetStaus_Name ? "scale(1.05)" : "scale(1)",
                                            transition: "transform 0.15s, box-shadow 0.15s, border 0.15s",
                                        }}
                                    >
                                        <div className="card-body d-flex flex-column align-items-center text-white">
                                            <div className="row">
                                                <div className="col-12">
                                                    <b style={{ fontSize: '20px', textAlign: 'center' }}>{count}</b>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-12">
                                                    <b style={{ fontSize: '16px', textAlign: 'center' }}>{DocSetStaus_Name}</b>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>
                <div className="col-11">
                    <div className='p-3 rounded-3 my-2 shadow-lg' style={{ backgroundColor: '#ffff' }}>
                        <div className='row  mb-2'>
                            <div className="col-3">
                                <div className="navbar-form nav-search">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            placeholder="ค้นหาเอกสาร..."
                                            className="form-control"
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                localStorage.setItem('searchTerm', e.target.value);
                                            }}
                                        />
                                        <span className="input-group-text">
                                            <i className="fas fa-search"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className='col-md-7 d-flex'>
                                <DateFilterTable
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                />
                            </div>
                            <div className="col-2 d-flex justify-content-end">
                                <button
                                    onClick={() => ExportSo(filteredItems)}
                                    className="btn btn-info text-white me-2"
                                    type="button"
                                    style={{ minWidth: '180px' }}
                                >
                                    พิมพ์รายงาน{name} <i className="fa fa-print"></i>
                                </button>
                                <button
                                    onClick={onPageInsert}
                                    className="btn btn-warning text-white ms-2"
                                    type="button"
                                    style={{ minWidth: '150px' }}
                                >
                                    สร้าง{name} <i className="fa fa-plus"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <DataTable
                        currentItems={currentItems}
                        onRowSelected={onRowSelected}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        handlePageChange={handlePageChange}
                        dataMasterList={filteredItems}
                        setDataMasterList={setFilteredItems}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={setItemsPerPage}
                        fieldMappings={{
                            no: 'Doc_No',
                            docBlNo: 'Doc_BL_No',
                            docInvNo: 'Doc_INV_No',
                            statusName: 'DocStatus_Name',
                            statusColour: 'DocStatus_Colour',
                            printedStatus: 'Printed_Status',
                            statusPaidName: 'DocStatusPaid_Name',
                            statusPaidColour: 'DocStatusPaid_Colour',
                            statusPaidTypeName: 'DocStatusPaid_Type_Name',
                            statusPaidTypeColour: 'DocStatusPaidType_Colour',
                            docIsPickingList: 'Doc_Is_PickingList',
                            date: 'Doc_Date',
                            apCode: 'AP_Code',
                            apName: 'AP_Name',
                            dlLineName: "DL_LineName",
                            dueDate: 'Doc_DueDate',
                            netTotal: 'NetTotal',
                            createdBy: 'Created_By_Name',
                            updatedBy: 'Update_By_Name'
                        }}
                        getReport={(docNo) => {
                            (async () => {
                                try {
                                    await getReport('so', docNo);
                                } catch (e) {
                                    console.error(e);
                                } finally {
                                    await initialize();
                                }
                            })();
                        }}
                    />
                </div>
            </div>
        </>
    );
}

export default Main;
